/**
 * One business in a collection grid: square logo tile, name, then type tag +
 * rating. Every value comes from the BusinessProfile in Redux — nothing here
 * is hardcoded.
 */

import React, { useState } from "react";
import { Star } from "lucide-react";
import { BusinessProfile } from "../../../../interfaces/BusinessObject";
import { useBusinessProfilePicture } from "../../../../hooks/useBusinessProfilePicture";
import { getInitials } from "../../../../services/helpers/InitialsHelper";
import CreateProductConfigHelper from "../../../../services/helpers/CreateProductConfigHelper";

interface CollectionBusinessCardProps {
  business: BusinessProfile;
  onOpen: (business: BusinessProfile) => void;
}

const CollectionBusinessCard: React.FC<CollectionBusinessCardProps> = ({
  business,
  onOpen,
}) => {
  const [imgError, setImgError] = useState(false);
  const logoUrl = useBusinessProfilePicture(business.businessId, business.logoUrl);
  const showLogo = Boolean(logoUrl) && !imgError;

  // Backend-set label wins; otherwise the shared id→label map. Never
  // re-title-cased here.
  const typeLabel =
    business.listingTypeLabel ||
    (business.listingType
      ? CreateProductConfigHelper.listingTypeIdToLabel(business.listingType)
      : "");

  // Hidden entirely when absent or zero — never "0.0".
  const rating = business.averageRating;
  const showRating = typeof rating === "number" && rating > 0;

  return (
    <button
      type="button"
      className="coll-card"
      onClick={() => onOpen(business)}
    >
      <span className="coll-card-logo">
        {showLogo ? (
          <img
            src={logoUrl as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="coll-card-initials" aria-hidden="true">
            {getInitials(business.name) || "?"}
          </span>
        )}
      </span>

      <span className="coll-card-name">{business.name}</span>

      <span className="coll-card-bottom">
        {typeLabel && <span className="coll-card-tag">{typeLabel}</span>}
        {showRating && (
          <span className="coll-card-rating">
            <Star size={12} fill="currentColor" strokeWidth={0} />
            {rating.toFixed(1)}
          </span>
        )}
      </span>
    </button>
  );
};

export default CollectionBusinessCard;
